import { SecurityVulnerability, JobStatusResponse, JobStreamEvent } from '../types';

// Read at call time, not import time: CRA inlines this at build time in a
// real production bundle either way, but reading it fresh here (rather than
// caching it in a module-level const) is what lets tests toggle
// process.env.REACT_APP_AI_BACKEND_URL between cases and have the service
// actually observe the change, since Jest evaluates this module once per
// test file.
function getBackendUrl(): string | undefined {
  return process.env.REACT_APP_AI_BACKEND_URL;
}

async function fetchWithTimeout(url: string, init: RequestInit | undefined, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timeout);
  }
}

/** Resolves false on ANY failure (missing config, network error, non-200) —
 * this is the sole gate for whether the Deep Analysis feature appears at
 * all, so it must never throw. */
export async function checkHealth(): Promise<boolean> {
  const backendUrl = getBackendUrl();
  if (!backendUrl) return false;
  try {
    const res = await fetchWithTimeout(`${backendUrl}/api/health`, undefined, 5000);
    return res.ok;
  } catch {
    return false;
  }
}

export interface SubmitAnalysisResponse {
  jobId: string;
}

export async function submitAnalysis(
  repoUrl: string,
  clientFindings: SecurityVulnerability[],
  githubToken?: string
): Promise<SubmitAnalysisResponse> {
  const backendUrl = getBackendUrl();
  if (!backendUrl) throw new Error('AI backend not configured');
  const res = await fetchWithTimeout(
    `${backendUrl}/api/analyze`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ repoUrl, githubToken, clientFindings }),
    },
    10000
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`AI backend ${res.status}: ${text}`);
  }
  return res.json();
}

export async function pollJob(jobId: string): Promise<JobStatusResponse> {
  const backendUrl = getBackendUrl();
  if (!backendUrl) throw new Error('AI backend not configured');
  const res = await fetchWithTimeout(`${backendUrl}/api/jobs/${jobId}`, undefined, 10000);
  if (!res.ok) throw new Error(`AI backend ${res.status}`);
  return res.json();
}

/** Opens an SSE connection for live progress. Its `result`/`error` payloads
 * are informational only — the caller (useAiVerification) always finalizes
 * via one pollJob() call on a terminal event, so this never needs to be the
 * authoritative source of the final findings/summary; it just has to signal
 * "something happened, go check." Returns a cleanup function that closes
 * the connection. */
export function streamJob(
  jobId: string,
  onEvent: (event: JobStreamEvent) => void,
  onError: () => void
): () => void {
  const backendUrl = getBackendUrl();
  if (!backendUrl) {
    onError();
    return () => {};
  }
  const source = new EventSource(`${backendUrl}/api/jobs/${jobId}/stream`);
  source.onmessage = (ev) => {
    let parsed: JobStreamEvent;
    try {
      parsed = JSON.parse(ev.data);
    } catch {
      return; // malformed event, ignore
    }
    onEvent(parsed);
    if (parsed.type === 'done' || parsed.type === 'error') {
      source.close();
    }
  };
  source.onerror = () => {
    source.close();
    onError();
  };
  return () => source.close();
}
